import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser, useSupportTickets, useCreateSupportTicket } from '../hooks/useApi';
import { useTranslation } from 'react-i18next';
import { useFormatDate } from '../hooks/useFormatDate';

const CATEGORY_KEYS = ['bug', 'feature', 'question', 'other'] as const;

const STATUS_STYLES: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700',
  'in-progress': 'bg-amber-100 text-amber-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-gray-100 text-gray-600'
};

export function SupportPage() {
  const { t } = useTranslation('support');
  const formatDate = useFormatDate();
  const navigate = useNavigate();
  const { data: user } = useUser();
  const { data: tickets, isLoading: ticketsLoading } = useSupportTickets();
  const createTicket = useCreateSupportTicket();

  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('question');
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState<string | null>(null);

  // Pre-fill from ?prefill=<base64-json-envelope> when ActionErrorPanel
  // navigates here. Envelope = { subject, body, category }.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const raw = searchParams.get('prefill');
    if (!raw) return;
    try {
      const json = decodeURIComponent(escape(atob(decodeURIComponent(raw))));
      const env = JSON.parse(json) as { subject?: string; body?: string; category?: string };
      if (env.subject) setSubject(env.subject);
      if (env.body) setMessage(env.body);
      if (env.category) setCategory(env.category);
    } catch { /* malformed prefill — ignore silently */ }
    // Clear the query param so a refresh doesn't re-prefill on top of edits
    const next = new URLSearchParams(searchParams);
    next.delete('prefill');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);
    createTicket.mutate(
      { subject, message, category },
      {
        onSuccess: (data) => {
          setSuccess(t('support.ticketCreated', { ticketId: data.ticketId }));
          setSubject('');
          setCategory('question');
          setMessage('');
        }
      }
    );
  };

  return (
    <div className="min-h-full p-4 sm:p-8 animate-fade-in">
    <div className="page-narrow space-y-8">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">{t('support.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('support.subtitle')}</p>
      </div>

      {/* Submit form */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-base font-semibold text-gray-800 mb-4">{t('support.submitRequest')}</h2>

        {success && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {success}
          </div>
        )}

        {createTicket.isError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {createTicket.error instanceof Error ? createTicket.error.message : t('support.failedToCreate')}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('support.emailLabel')}</label>
            <input
              type="email"
              value={user?.email ?? ''}
              disabled
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('support.subjectLabel')}</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              maxLength={200}
              placeholder={t('support.subjectPlaceholder')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('support.categoryLabel')}</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {CATEGORY_KEYS.map((c) => (
                <option key={c} value={c}>{t(`support.categories.${c}`)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('support.messageLabel')}</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              maxLength={5000}
              rows={5}
              placeholder={t('support.messagePlaceholder')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none"
            />
            <p className="mt-1 text-xs text-gray-400">{t('support.charCount', { count: message.length })}</p>
          </div>

          <button
            type="submit"
            disabled={createTicket.isPending || !subject.trim() || !message.trim()}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
          >
            {createTicket.isPending ? t('support.submitting') : t('support.submitTicket')}
          </button>
        </form>
      </div>

      {/* My tickets */}
      <div>
        <h2 className="text-base font-semibold text-gray-800 mb-3">{t('support.myTickets')}</h2>
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{t('support.ticketIdHeader')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{t('support.subjectHeader')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{t('support.statusHeader')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{t('support.dateHeader')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ticketsLoading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">
                    {t('support.loading')}
                  </td>
                </tr>
              ) : !tickets || tickets.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">
                    {t('support.noTickets')}
                  </td>
                </tr>
              ) : (
                tickets.map((ticket) => (
                  <tr key={ticket.ticketId} onClick={() => navigate(`/support/${ticket.ticketId}`)} className="cursor-pointer hover:bg-gray-50 transition-colors">
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-700">
                      {ticket.ticketId}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {ticket.subject}
                      {ticket.hasReply && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-600">
                          {t('support.replied')}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[ticket.status] ?? STATUS_STYLES.open}`}>
                        {ticket.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                      {ticket.createdAt ? formatDate(ticket.createdAt) : '\u2014'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </div>
  );
}
